/** @file OrientationCube: the viewport's Unity-style view cube; turns with the camera, snaps on click. */
import { useEffect, useRef, useState, type ReactElement, type RefObject } from 'react';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { CubeFaceShape } from './CubeFaceShape';
import type { CubeFace } from './cubeFaces';
import { cubeFaceView } from './cubeFaceView';
import { CubeGround } from './CubeGround';
import { cubeProjection, type CubeProjection } from './cubeProjection';
import { CubeYawStops } from './CubeYawStops';
import { toggleProfilePitch } from './toggleProfilePitch';
import { useCompassDrag } from './useCompassDrag';
import { VIEWPORT_COLORS } from './viewportColors';
import { VIEWPORT_STYLES } from './viewportStyles';

/** Box size, cube centre and px per half-edge; the ground ring's radius in half-edges. */
const SIZE = 104;
const CX = SIZE / 2;
const CY = 44;
const SCALE = 19;
const RING = 1.9;
/** The camera used before a bundle is loaded (the viewport's own fallbacks: profile pitch 30, no clamp). */
const FALLBACK: { pitch: number; pitchRange: [number, number] } = { pitch: 30, pitchRange: [-90, 90] };

/** Yaw stops on the ring: the puppet's `facing.steps` (2..16), else eight (0, 45, ... 315). */
const stopsOf = (steps: number | 'free' | undefined): number => (typeof steps === 'number' && steps >= 2 && steps <= 16 ? steps : 8);

/** The px box around the visible faces (the cube's silhouette, roughly). */
function screenBox(proj: CubeProjection): [number, number, number, number] {
  const pts = proj.faces.flatMap((f) => f.quad);
  const xs = pts.map((p) => CX + p[0] * SCALE);
  const ys = pts.map((p) => CY + p[1] * SCALE);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

/** Keeps a press on the cube from reaching the viewport's native pointer listeners (select, orbit, context menu). */
function useIsolated(): RefObject<SVGSVGElement | null> {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;
    const stop = (e: Event): void => { e.stopPropagation(); };
    el.addEventListener('pointerdown', stop);
    el.addEventListener('contextmenu', stop);
    return () => { el.removeEventListener('pointerdown', stop); el.removeEventListener('contextmenu', stop); };
  }, []);
  return ref;
}

/**
 * The cube is projected from the store's camera yaw / pitch (`cubeProjection`), so it turns as the user orbits and
 * tilts. A side face sets the yaw, up / down the pitch at the ends of the profile's `pitchRange` (`cubeFaceView`); the
 * centre dot toggles between the profile pitch and the pitch before it (`toggleProfilePitch`, shared with Numpad5).
 * The ground ring carries the +X / +Y labels, the body-facing marker (`facingDeg`, as the runtime last drew it) and
 * one fixed-yaw button per facing step (`CubeYawStops`, drawn before the faces: the cube hides the far ones); a left
 * drag anywhere on the cube or its ring orbits the yaw (`useCompassDrag`), a click without travel keeps the face snap.
 */
export function OrientationCube({ facingDeg }: { facingDeg: number }): ReactElement {
  const store = useEditorStore();
  const yaw = useEditor((s) => s.camera.yawDeg);
  const pitch = useEditor((s) => s.camera.pitchDeg);
  const cam = useEditor((s) => s.preview.bundle?.profile.camera ?? null) ?? FALLBACK;
  const steps = useEditor((s) => s.puppet.doc?.puppet.facing.steps);
  const [hover, setHover] = useState<string | null>(null);
  const ref = useIsolated();
  useCompassDrag(ref, store);
  const proj = cubeProjection(yaw, pitch);
  const pick = (face: CubeFace): void => { store.getState().setCamera(cubeFaceView(face, cam.pitchRange)); };
  const toggle = (): void => { toggleProfilePitch(store, cam.pitch); };
  const ground = { yawDeg: yaw, pitchDeg: pitch, facingDeg, scale: SCALE, cx: CX, cy: CY, radius: RING, box: screenBox(proj) };
  return (
    <svg ref={ref} style={VIEWPORT_STYLES.cube} width={SIZE} height={SIZE} aria-label="orientation cube" data-front={proj.front}>
      <CubeGround {...ground} part="ring" />
      <CubeYawStops {...ground} steps={stopsOf(steps)} onPick={(yawDeg) => { store.getState().setCamera({ yawDeg }); }} />
      {proj.faces.map((f) => (
        <CubeFaceShape key={f.id} face={f} scale={SCALE} cx={CX} cy={CY} hovered={hover === f.id}
          onHover={(on) => { setHover(on ? f.id : null); }} onPick={() => { pick(f.face); }} />
      ))}
      <circle role="button" aria-label="profile pitch" cx={CX} cy={CY} r={3} onClick={toggle}
        fill={hover === 'centre' ? VIEWPORT_COLORS.hover : VIEWPORT_COLORS.centre} stroke="#141a17"
        style={{ cursor: 'pointer', pointerEvents: 'visiblePainted' }}
        onPointerEnter={() => { setHover('centre'); }} onPointerLeave={() => { setHover(null); }}>
        <title>{`Toggle the profile pitch (${String(cam.pitch)} deg) and back`}</title>
      </circle>
      <CubeGround {...ground} part="marker" />
    </svg>
  );
}
